import React from 'react';

interface ToggleOption {
  code: string;
  label: string;
}

interface DataToggleProps {
  options: ToggleOption[];
  currentIndex: number;
  onChange: (index: number) => void;
  columnLabel?: string;
  size?: 'sm' | 'md';
}

/**
 * DataToggle - A toggle component for switching between data options
 *
 * @description A clean, simple toggle component that allows users to switch between different data views
 * or options with full accessibility compliance.
 */
const DataToggle: React.FC<DataToggleProps> = ({
  options,
  currentIndex,
  onChange,
  columnLabel = 'Toggle options',
  size = 'sm',
}) => {
  const handleOptionClick = (index: number) => {
    onChange(index);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOptionClick(index);
    }
  };

  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';
  const padding = size === 'sm' ? 'px-2 py-0.5' : 'px-3 py-1';

  return (
    <div
      className="flex items-center gap-1"
      role="tablist"
      aria-label={`${columnLabel} toggle options`}
    >
      {options.map((option, index) => (
        <button
          key={option.code}
          type="button"
          className={`${padding} ${textSize} transition-all duration-200 rounded-md font-medium focus:outline-none ${
            index === currentIndex
              ? 'bg-blue-50 text-blue-700 font-semibold shadow-sm'
              : 'text-neutral-600 hover:text-neutral-800 hover:bg-neutral-50'
          }`}
          onClick={() => handleOptionClick(index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          role="tab"
          aria-selected={index === currentIndex}
          aria-label={`${option.label}${
            index === currentIndex ? ' (selected)' : ''
          }`}
          title={option.label}
          tabIndex={index === currentIndex ? 0 : -1}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

export default DataToggle;
